import React, { useState } from "react";
import {
  ShieldCheck,
  AlertTriangle,
  CheckCircle2,
  Download,
  Info,
  Cpu,
  Key,
  Server,
  Sliders,
  Award,
  ArrowUpRight,
  ChevronDown,
  FileCheck
} from "lucide-react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  PieChart,
  Pie,
  Cell
} from "recharts";

// --- Mock Data ---

const historicalTrendData = [
  { date: "May 1", score: 77 },
  { date: "May 6", score: 80 },
  { date: "May 11", score: 81 },
  { date: "May 16", score: 83 },
  { date: "May 21", score: 85 },
  { date: "May 26", score: 88 },
  { date: "May 31", score: 91 },
];

const distributionData = [
  { name: "Excellent (90-100)", value: 38, color: "#10B981" },
  { name: "Very Strong (80-89)", value: 32, color: "#14B8A6" },
  { name: "Strong (70-79)", value: 18, color: "#84CC16" },
  { name: "Fair (50-69)", value: 9, color: "#F59E0B" },
  { name: "Poor (<50)", value: 3, color: "#EF4444" },
];

const componentScores = [
  { id: "algo", name: "Algorithm Security", score: 95, weight: "25%", color: "#6366F1", icon: Cpu, trend: "+7.2" },
  { id: "key", name: "Key & Identity Security", score: 90, weight: "25%", color: "#3B82F6", icon: Key, trend: "+5.8" },
  { id: "infra", name: "Infrastructure Security", score: 88, weight: "20%", color: "#10B981", icon: Server, trend: "+4.1" },
  { id: "ops", name: "Operational Security", score: 92, weight: "15%", color: "#F97316", icon: Sliders, trend: "+6.9" },
  { id: "comp", name: "Compliance & Governance", score: 89, weight: "15%", color: "#8B5CF6", icon: FileCheck, trend: "+3.6" },
];

const assetGroups = [
  { name: "Applications", score: 93 },
  { name: "Servers & Endpoints", score: 90 },
  { name: "Network Infrastructure", score: 87 },
  { name: "Mobile Devices", score: 92 },
  { name: "Cloud Services", score: 89 },
  { name: "IoT / OT Devices", score: 85 },
];

const positiveContributors = [
  { title: "PQC Adoption", value: "+15.2" },
  { title: "Zero Critical Alerts", value: "+10.8" },
  { title: "Strong Key Management", value: "+8.7" },
  { title: "Complete Compliance", value: "+6.3" },
  { title: "High Verification Success", value: "+5.1" },
];

const riskDetractors = [
  { title: "Legacy Algorithms Detected", value: "-7.6" },
  { title: "Expired Certificates", value: "-4.3" },
  { title: "Unused Keys", value: "-3.8" },
  { title: "High Risk Assets", value: "-2.9" },
  { title: "Incomplete Policies", value: "-2.1" },
];

const heatmapData = [
  { env: "Production", apps: 92, infra: 89, data: 95, identities: 93, certs: 91 },
  { env: "Staging", apps: 90, infra: 86, data: 90, identities: 89, certs: 88 },
  { env: "Development", apps: 85, infra: 82, data: 86, identities: 87, certs: 83 },
  { env: "QA", apps: 88, infra: 84, data: 87, identities: 86, certs: 85 },
];

const scoreHistory = [
  { date: "May 31, 2026", score: "91/100", trend: "+6.4" },
  { date: "Apr 30, 2026", score: "85/100", trend: "+4.2" },
  { date: "Mar 31, 2026", score: "81/100", trend: "+3.7" },
  { date: "Feb 28, 2026", score: "77/100", trend: "+5.1" },
];

export default function TrustScore() {
  const [showCalcModal, setShowCalcModal] = useState(false);

  const getHeatmapBg = (score) => {
    if (score >= 90) return "bg-emerald-500 text-white";
    if (score >= 80) return "bg-emerald-300 text-slate-900";
    if (score >= 70) return "bg-amber-200 text-slate-900";
    if (score >= 50) return "bg-orange-200 text-slate-900";
    return "bg-rose-300 text-slate-900";
  };

  return (
    <div className="min-h-screen bg-white p-0 text-slate-800 font-sans space-y-2 text-xs" style={{padding:0}}>

      {/* ================= HEADER BAR ================= */}
      <div className="flex justify-end items-center gap-3 px-4 py-1">
          <button
            onClick={() => setShowCalcModal(!showCalcModal)}
            className="text-xs font-semibold text-indigo-600 hover:text-indigo-800 flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-indigo-50 transition"
          >
            <Info className="w-4 h-4" />
            How Trust Score is calculated?
          </button>
          <button className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium px-4 py-2 rounded-lg flex items-center gap-2 transition shadow-sm">
            <Download className="w-4 h-4" />
            Export Report
          </button>
      </div>

      {/* ================= HERO GAUGE & COMPONENT HIGHLIGHTS ================= */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 px-4">

        {/* Hero Card with Score Gauge */}
        <div className="lg:col-span-4 bg-white rounded-xl border border-slate-200 p-3 shadow-sm flex flex-col md:flex-row gap-4 items-center">
          
          {/* Semi-Circle SVG Gauge */}
          <div className="flex flex-col items-center justify-center shrink-0 w-36">
            <div className="relative w-32" style={{ height: "72px" }}>
              <svg viewBox="0 0 100 50" className="w-full" style={{ display: "block" }}>
                <path d="M 10 50 A 40 40 0 0 1 90 50" fill="none" stroke="#E2E8F0" strokeWidth="10" strokeLinecap="round" />
                <path d="M 10 50 A 40 40 0 0 1 90 50" fill="none" stroke="#10B981" strokeWidth="10" strokeLinecap="round"
                  strokeDasharray="125.6" strokeDashoffset="11.3" className="transition-all duration-700" />
              </svg>
              <div className="absolute bottom-0 w-full text-center">
                <span className="text-3xl font-extrabold text-slate-900 leading-none">91</span>
                <span className="text-xs text-slate-400 font-medium">/100</span>
              </div>
            </div>
            <div className="mt-3 bg-emerald-50 text-emerald-700 font-bold text-xs px-3 py-1 rounded-full flex items-center gap-1.5 border border-emerald-200">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
              Very Strong
            </div>
          </div>

          {/* Description & KPI Metrics */}
          <div className="flex-1 space-y-4">
            <p className="text-xs text-slate-600 leading-relaxed">
              <strong className="text-slate-800">Excellent! Your organization maintains a high digital trust rating.</strong><br/>
              Your infrastructure, keys, and signed assets are quantum-safe aligned with standard governance.
            </p>
            <div className="border-t border-slate-100 pt-3 space-y-2 text-xs">
              <div className="flex justify-between items-center text-slate-500">
                <span>Score Trend (vs last 30 days)</span>
                <span className="text-emerald-600 font-bold flex items-center gap-0.5">
                  <ArrowUpRight className="w-3.5 h-3.5" /> 6.4%
                </span>
              </div>
              <div className="flex justify-between items-center text-slate-500">
                <span>Industry Average</span>
                <span className="font-bold text-slate-800">72 / 100</span>
              </div>
              <div className="flex justify-between items-center text-slate-500">
                <span>Your Last Score</span>
                <span className="font-bold text-slate-800">85 / 100</span>
              </div>
              <div className="flex justify-between items-center text-slate-500">
                <span>Best Score Recorded</span>
                <span className="font-bold text-slate-800">93 / 100</span>
              </div>
            </div>
          </div>
        </div>

        {/* 5 Component Mini Status Cards */}
        <div className="lg:col-span-8 grid grid-cols-2 sm:grid-cols-5 lg:grid-cols-5 gap-2">
          {componentScores.map((comp) => {
            const IconComponent = comp.icon;
            return (
              <div key={comp.id} className="bg-white rounded-xl border border-slate-200 p-3 shadow-sm flex flex-col items-center hover:border-slate-300 transition">
                <div className="flex items-center justify-center w-16 h-16 rounded-xl bg-slate-50 mb-2" style={{ color: comp.color }}>
                  <IconComponent className="w-7 h-7" />
                </div>
                <div className="text-center">
                  <h4 className="text-sm font-bold text-slate-700 leading-tight">{comp.name}</h4>
                  <div className="text-2xl font-extrabold text-slate-900 mt-1">
                    {comp.score}<span className="text-xs text-slate-400 font-normal">/100</span>
                  </div>
                  <div className="text-[10px] font-semibold text-emerald-600 mt-0.5 flex items-center justify-center gap-0.5">
                    <ArrowUpRight className="w-3 h-3" /> {comp.trend}
                  </div>
                </div>
                <div style={{ height: "26px", width: "100%", marginTop: "8px" }}>
                  <svg viewBox="0 0 100 30" width="100%" height="100%" preserveAspectRatio="none">
                    <defs>
                      <linearGradient id={`grad-${comp.id}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={comp.color} stopOpacity="0.2" />
                        <stop offset="100%" stopColor={comp.color} stopOpacity="0" />
                      </linearGradient>
                    </defs>
                    <path d="M0,18 L20,12 L40,20 L60,8 L80,14 L100,4 L100,30 L0,30 Z" fill={`url(#grad-${comp.id})`} />
                    <polyline points="0,18 20,12 40,20 60,8 80,14 100,4" fill="none" stroke={comp.color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    {[[0,18],[20,12],[40,20],[60,8],[80,14],[100,4]].map(([x,y]) => (
                      <circle key={x} cx={x} cy={y} r="2.5" fill={comp.color} stroke={comp.color} strokeWidth="1.5" />
                    ))}
                  </svg>
                </div>
              </div>
            );
          })}
        </div>

      </div>

      {/* ================= MIDDLE CHARTS & ANALYSIS ================= */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 px-4 mt-4">

      <div className="lg:col-span-3 bg-white rounded-xl p-3.5 border border-slate-200 shadow-sm flex flex-col justify-between">
        <div className="flex justify-between items-center mb-2">
          <h3 className="font-bold text-slate-900 text-xs">Trust Score Over Time</h3>
          <button className="text-[10px] text-slate-600 font-medium border border-slate-200 rounded-lg px-2 py-1 bg-slate-50 hover:bg-slate-100 flex items-center gap-1 transition">
            Last 30 Days
            <ChevronDown className="w-3 h-3 text-slate-400" />
          </button>
        </div>
        <div className="h-28 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={historicalTrendData} margin={{ top: 8, right: 8, left: -22, bottom: 0 }}>
              <XAxis dataKey="date" tick={{ fill: '#94A3B8', fontSize: 8 }} tickLine={false} axisLine={{ stroke: '#E2E8F0' }} />
              <YAxis domain={[60, 100]} tick={{ fill: '#94A3B8', fontSize: 8 }} tickLine={false} axisLine={{ stroke: '#E2E8F0' }} />
              <Tooltip />
              <Line type="monotone" dataKey="score" stroke="#6366F1" strokeWidth={2} dot={{ fill: "#6366F1", r: 2.5, strokeWidth: 0 }} activeDot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

       {/* Top Positive Contributors */}
<div className="lg:col-span-3 bg-white rounded-xl p-3 border border-slate-200 shadow-sm flex flex-col items-start text-left">
  <h3 className="font-bold text-slate-900 text-xs mb-3 text-left self-start">Top Positive Contributors</h3>
  <ul className="space-y-2.5 w-full">
    {positiveContributors.map((item) => (
      <li key={item.title} className="flex items-center w-full">
        <span className="w-4 h-4 rounded-full bg-emerald-500 flex items-center justify-center shrink-0 mr-1.5">
          <svg className="w-2.5 h-2.5 text-white stroke-[3]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </span>
        <span className="text-slate-800 font-semibold text-[11px] text-left">{item.title}</span>
        <span className="font-bold text-emerald-500 text-[11px] ml-auto">{item.value}</span>
      </li>
    ))}
  </ul>
</div>

        {/* Top Risk Detractors */}
        <div className="lg:col-span-3 bg-white rounded-xl p-3 border border-slate-200 shadow-sm flex flex-col">
          <h3 className="font-bold text-slate-900 text-xs mb-3">Risk Detractors</h3>
          <ul className="space-y-2.5 w-full">
            {riskDetractors.map((item) => (
              <li key={item.title} className="flex items-center w-full">
                <span className="w-4 h-4 rounded-full bg-rose-500 flex items-center justify-center shrink-0 mr-1.5">
                  <AlertTriangle className="w-2.5 h-2.5 text-white" />
                </span>
                <span className="text-slate-800 font-semibold text-[11px] text-left">{item.title}</span>
                <span className="font-bold text-rose-500 text-[11px] ml-auto">{item.value}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Score Distribution Donut */}
        <div className="lg:col-span-3 bg-white rounded-xl p-4 border border-slate-200 shadow-sm flex flex-col justify-between">
          <h3 className="font-bold text-slate-900 text-xs mb-3">Score Distribution</h3>
          <div className="flex items-center justify-between gap-3">
            <div className="relative w-28 h-28 shrink-0 flex items-center justify-center">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={distributionData} innerRadius={32} outerRadius={44} paddingAngle={0} dataKey="value" startAngle={90} endAngle={-270}>
                    {distributionData.map((entry) => (
                      <Cell key={entry.name} fill={entry.color} stroke="none" />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none text-center">
                <span className="text-[9px] text-slate-400 font-medium leading-none">Total</span>
                <span className="text-base font-extrabold text-slate-900 leading-tight my-0.5">126</span>
                <span className="text-[9px] text-slate-400 font-medium leading-none">Assets</span>
              </div>
            </div>
            <div className="space-y-2 w-full">
              {distributionData.map((item) => (
                <div key={item.name} className="flex items-center justify-between text-[10px]">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: item.color }} />
                    <span className="text-slate-600 font-medium truncate">{item.name}</span>
                  </div>
                  <span className="font-bold text-slate-900 pl-2">{item.value}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 px-4 mt-4">

        {/* Component Progress Bars */}
        <div className="lg:col-span-4 bg-white rounded-xl p-4 border border-slate-200 shadow-sm space-y-3">
          <h3 className="font-bold text-slate-900 text-xs">Trust Score by Component</h3>
          <div className="space-y-2.5">
            {componentScores.map((comp) => (
              <div key={comp.id} className="space-y-1">
                <div className="flex justify-between text-[11px] font-medium text-slate-700">
                  <span>{comp.name}</span>
                  <span className="font-bold text-slate-900">{comp.score}/100</span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-500" style={{ width: `${comp.score}%`, backgroundColor: comp.color }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Asset Group Progress Bars */}
        <div className="lg:col-span-4 bg-white rounded-xl p-4 border border-slate-200 shadow-sm space-y-3">
          <div className="flex justify-between items-center">
            <h3 className="font-bold text-slate-900 text-xs">Trust Score by Asset Group</h3>
            <span className="text-[10px] text-slate-400">6 Groups</span>
          </div>
          <div className="space-y-2">
            {assetGroups.map((group) => (
              <div key={group.name} className="space-y-1">
                <div className="flex justify-between text-[11px] text-slate-700">
                  <span>{group.name}</span>
                  <span className="font-bold text-slate-900">{group.score}/100</span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
                  <div className="bg-teal-500 h-full rounded-full" style={{ width: `${group.score}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Heatmap Table */}
        <div className="lg:col-span-4 bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-4 py-2.5 border-b border-slate-100">
            <h3 className="font-bold text-slate-900 text-xs">Trust Score Heatmap</h3>
          </div>
          <table className="w-full text-[10px] text-center" style={{ borderCollapse: "collapse" }}>
            <thead>
              <tr className="bg-slate-50 text-slate-500 font-semibold">
                <th className="py-2 px-3 text-left font-semibold border-b border-r border-slate-200">Environment</th>
                <th className="py-2 px-2 border-b border-r border-slate-200">Apps</th>
                <th className="py-2 px-2 border-b border-r border-slate-200">Infra</th>
                <th className="py-2 px-2 border-b border-r border-slate-200">Data</th>
                <th className="py-2 px-2 border-b border-r border-slate-200">Identities</th>
                <th className="py-2 px-2 border-b border-slate-200">Certs</th>
              </tr>
            </thead>
            <tbody>
              {heatmapData.map((row) => (
                <tr key={row.env} className="border-b border-slate-100 last:border-none">
                  <td className="py-2.5 px-3 text-left font-semibold text-slate-700 border-r border-slate-100 bg-slate-50">{row.env}</td>
                  <td className={`py-2.5 px-2 font-bold border-r border-white/30 ${getHeatmapBg(row.apps)}`}>{row.apps}</td>
                  <td className={`py-2.5 px-2 font-bold border-r border-white/30 ${getHeatmapBg(row.infra)}`}>{row.infra}</td>
                  <td className={`py-2.5 px-2 font-bold border-r border-white/30 ${getHeatmapBg(row.data)}`}>{row.data}</td>
                  <td className={`py-2.5 px-2 font-bold border-r border-white/30 ${getHeatmapBg(row.identities)}`}>{row.identities}</td>
                  <td className={`py-2.5 px-2 font-bold ${getHeatmapBg(row.certs)}`}>{row.certs}</td>
                </tr>
              ))}
            </tbody>
          </table>
        
      {/* Legend Footer */}
  <div className="flex flex-wrap items-center gap-1.5 px-2 pb-2 pt-1.5 text-[8px] text-slate-600 font-medium">
    <div className="flex items-center gap-0.5">
      <span className="w-3 h-1.5 rounded-sm bg-emerald-500 inline-block" />
      <span className="font-bold text-slate-900">90–100</span>
      <span className="text-slate-500">Excellent</span>
    </div>
    <div className="flex items-center gap-0.5">
      <span className="w-3 h-1.5 rounded-sm bg-emerald-300 inline-block" />
      <span className="font-bold text-slate-900">80–89</span>
      <span className="text-slate-500">Very Strong</span>
    </div>
    <div className="flex items-center gap-0.5">
      <span className="w-3 h-1.5 rounded-sm bg-yellow-200 inline-block" />
      <span className="font-bold text-slate-900">70–79</span>
      <span className="text-slate-500">Strong</span>
    </div>
    <div className="flex items-center gap-0.5">
      <span className="w-3 h-1.5 rounded-sm bg-amber-300 inline-block" />
      <span className="font-bold text-slate-900">50–69</span>
      <span className="text-slate-500">Fair</span>
    </div>
    <div className="flex items-center gap-0.5">
      <span className="w-3 h-1.5 rounded-sm bg-rose-300 inline-block" />
      <span className="font-bold text-slate-900">&lt;50</span>
      <span className="text-slate-500">Poor</span>
    </div>
  </div>
  </div>

      </div>

      {/* ================= BOTTOM ROW: INSIGHTS, BENCHMARKING & HISTORY ================= */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-4 px-4 mt-4 pb-4">

        {/* Key Insights */}
        <div className="lg:col-span-3 bg-white rounded-xl p-4 border border-slate-200 shadow-sm flex flex-col justify-between">
          <div className="space-y-3">
            <h3 className="font-bold text-slate-900 text-xs">Trust Insights</h3>
            <ul className="space-y-2 text-[11px] text-slate-700">
              <li className="flex items-start gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                <span><strong>95%</strong> of signatures are quantum-safe.</span>
              </li>
              <li className="flex items-start gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" />
                <span><strong>3 legacy algorithms detected.</strong> Migration needed.</span>
              </li>
              <li className="flex items-start gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                <span>All high-priority CVEs remediated.</span>
              </li>
            </ul>
          </div>
          <button className="text-indigo-600 hover:underline text-[11px] font-bold text-left mt-3">View all insights &rarr;</button>
        </div>

        {/* Recommended Actions */}
        <div className="lg:col-span-3 bg-white rounded-xl p-4 border border-slate-200 shadow-sm flex flex-col justify-between">
          <div className="space-y-3">
            <h3 className="font-bold text-slate-900 text-xs">Recommended Actions</h3>
            <div className="space-y-2 text-[10px]">
              <div className="flex items-center justify-between bg-slate-50 p-2 rounded-lg border border-slate-100">
                <span className="font-medium text-slate-700">Migrate 3 legacy algorithms</span>
                <span className="bg-rose-50 text-rose-600 px-1.5 py-0.5 rounded font-bold">High</span>
              </div>
              <div className="flex items-center justify-between bg-slate-50 p-2 rounded-lg border border-slate-100">
                <span className="font-medium text-slate-700">Renew 7 expiring certs</span>
                <span className="bg-amber-50 text-amber-600 px-1.5 py-0.5 rounded font-bold">Med</span>
              </div>
              <div className="flex items-center justify-between bg-slate-50 p-2 rounded-lg border border-slate-100">
                <span className="font-medium text-slate-700">Disable 12 unused keys</span>
                <span className="bg-amber-50 text-amber-600 px-1.5 py-0.5 rounded font-bold">Med</span>
              </div>
            </div>
          </div>
          <button className="text-indigo-600 hover:underline text-[11px] font-bold text-left mt-3">View action plan &rarr;</button>
        </div>

        {/* Industry Benchmarking */}
        <div className="lg:col-span-3 bg-white rounded-xl p-4 border border-slate-200 shadow-sm flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-1.5 mb-2">
              <Award className="w-4 h-4 text-indigo-600" />
              <h3 className="font-bold text-slate-900 text-xs">Benchmarking</h3>
            </div>
            <p className="text-[11px] text-slate-600 mb-4 leading-normal">
              Your score ranks higher than <strong className="text-slate-900">82%</strong> of peer organizations in Tech & Cybersecurity.
            </p>
            <div className="relative w-full my-2">
              <div className="w-full bg-slate-100 rounded-full h-2.5">
                <div className="bg-indigo-600 h-2.5 rounded-full" style={{ width: "82%" }} />
              </div>
              <div className="absolute -top-6 left-[82%] -translate-x-1/2 bg-indigo-600 text-white text-[9px] font-extrabold px-1.5 py-0.5 rounded shadow">
                91
              </div>
            </div>
            <div className="flex justify-between text-[9px] text-slate-400 font-medium">
              <span>0</span><span>25</span><span>50</span><span>75</span><span>100</span>
            </div>
          </div>
        </div>

        {/* History Log */}
        <div className="lg:col-span-3 bg-white rounded-xl p-4 border border-slate-200 shadow-sm flex flex-col justify-between">
          <div>
            <h3 className="font-bold text-slate-900 text-xs mb-3">Trust Score History</h3>
            <ul className="space-y-2 text-[11px] text-slate-700">
              {scoreHistory.map((item) => (
                <li key={item.date} className="flex justify-between items-center pb-1 border-b border-slate-50 last:border-none">
                  <span className="text-slate-500 text-[10px]">{item.date}</span>
                  <span className="font-bold text-slate-900">{item.score}</span>
                  <span className="text-emerald-600 font-semibold text-[10px]">↑ {item.trend}</span>
                </li>
              ))}
            </ul>
          </div>
          <button className="text-indigo-600 hover:underline text-[11px] font-bold text-left mt-3">Full history &rarr;</button>
        </div>

      </div>

    </div>
  );
}